import { Box, Link, Stack, Typography } from "@mui/material";
import {
  ChevronDown as KeyboardArrowDown,
  ChevronRight as KeyboardArrowRight,
} from "lucide-react";
import { FC, useCallback } from "react";
import { Link as RouteLink } from "react-router-dom";
import { CellProps, Column, Row } from "react-table";

import { LoanApplicationsType } from "api/loanApplications/types";
import { getLoanPackages } from "api/loanPackages";
import { loanPackagesKeys } from "api/loanPackages/queries";
import { LoanPackagesType } from "api/loanPackages/types";
import { QueryParamsType } from "api/utils";
import Table from "components/Table";
import { precisePercentageFormatter } from "components/Table/formatters/percentageFormatter";
import { priceFormatter } from "components/Table/formatters/priceFormatter";
import useRemoteTableLogic from "components/Table/useRemoteTableLogic";

import LoanApplicationsTable from "./SubTables/LoanApplicationsTable";
import { useLoanPackagesFilter } from "./useLoanPackagesFilter";

interface LoanPackagesTableProps {
  filters?: QueryParamsType<LoanPackagesType>;
}

const LoanPackagesTable: FC<LoanPackagesTableProps> = ({
  filters: filtersFromProps,
}) => {
  const filterHook = useLoanPackagesFilter();
  const filters = filtersFromProps ?? filterHook.filters;

  const { data, status, sortOptions, paginationOptions } = useRemoteTableLogic(
    loanPackagesKeys.list(),
    getLoanPackages,
    filters,
  );

  const columns: Column<LoanPackagesType>[] = [
    {
      accessor: "name",
      Header: "Deal Name",
      Aggregated: ({ row, value }: CellProps<LoanPackagesType>) => {
        return (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            {...row.getToggleRowExpandedProps()}
          >
            <>
              {row.original.loanApplications?.length}
              {row.isExpanded ? (
                <KeyboardArrowDown size={18} style={{ marginRight: 8 }} />
              ) : (
                <KeyboardArrowRight size={18} style={{ marginRight: 8 }} />
              )}
              {value}
            </>
          </Stack>
        );
      },
      Cell: () => null,
    },
    {
      accessor: "loanAmount",
      Header: "Loan Amount",
      Aggregated: ({ value }: CellProps<LoanPackagesType>) => (
        <Box>{priceFormatter(value)}</Box>
      ),
      Cell: () => null,
    },
    {
      accessor: "monthlyPayment",
      Header: "Monthly Payment",
      Aggregated: ({ value }: CellProps<LoanPackagesType>) => (
        <Box>{priceFormatter(value)}</Box>
      ),
      Cell: () => null,
    },
    {
      accessor: "term",
      Header: "Term",
      Cell: () => null,
    },
    {
      accessor: "interestRate",
      Header: "Interest Rate",
      Aggregated: ({ value }: CellProps<LoanPackagesType>) => (
        <Box>{precisePercentageFormatter(value)}</Box>
      ),
      Cell: () => null,
    },
    {
      accessor: "originationFee",
      Header: "Origination Fee",
      Aggregated: ({ value }: CellProps<LoanPackagesType>) => (
        <Box>{priceFormatter(value)}</Box>
      ),
      Cell: () => null,
    },
    {
      accessor: "apr",
      Header: "APR",
      Aggregated: ({ value }: CellProps<LoanPackagesType>) => (
        <Box>{precisePercentageFormatter(value)}</Box>
      ),
      Cell: () => null,
    },
    {
      id: "reports",
      Header: "Reports",
      Aggregated: ({ row }: CellProps<LoanPackagesType>) => (
        <Stack direction="column" spacing={0.5}>
          <Link
            component={RouteLink}
            to={`/credit-evaluations/${row.original.creditEvaluation?._id}`}
          >
            Credit Evaluation
          </Link>
          <Link
            component={RouteLink}
            to={`/customers/${row.original.customer?._id}`}
          >
            Customer Details
          </Link>

          <Link
            component="a"
            href={`https://app.hubspot.com/contacts/44366077/contact/${row.original.customer?.hubspotId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Hubspot Contact Details
          </Link>
        </Stack>
      ),
      Cell: () => null,
    },
  ];

  const getSubRows = useCallback(
    (loanPackageRow: any) =>
      loanPackageRow.loanApplications?.length
        ? [loanPackageRow.loanApplications]
        : [],
    [],
  );

  return (
    <Stack spacing={2}>
      <Table
        data={data}
        columns={columns}
        sort={sortOptions}
        pagination={paginationOptions}
        status={status}
        getSubRows={getSubRows}
        subComponent={(row: Row<LoanApplicationsType[]>) => {
          return (
            <Box component="td" colSpan={columns.length + 1} sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Loan Applications
              </Typography>
              <LoanApplicationsTable data={row.original} status={status} />
            </Box>
          );
        }}
      />
    </Stack>
  );
};

export default LoanPackagesTable;
